/**
 * Sector Management Card Component
 *
 * Enhanced sector card for command room dashboard with:
 * - Participant list with "Assign as Organizer" button
 * - Share room button
 * - Edit boundary button
 * - Status indicator
 * - Mini boundary preview
 */

import { useState } from 'react'
import type { Participant, GeoLocation } from '@/lib/types'
import type { ExtendedSectorInfo } from '@/hooks/useHierarchy'
import type { RemoteCommandPayload } from '@/lib/hierarchy/commands'

interface SectorManagementCardProps {
  sector: ExtendedSectorInfo
  participants?: Participant[]
  onAssignOrganizer?: (participantPeerId: string, participantPublicKey: string) => void
  onShareSector?: () => void
  onEditBoundary?: () => void
  onOpenSector?: () => void
  onDeleteSector?: () => void
  onSendCommand?: (command: RemoteCommandPayload) => void
  commandBoundary?: { center: GeoLocation; radiusMeters: number }
  isSelected?: boolean
}

export function SectorManagementCard({
  sector,
  participants = [],
  onAssignOrganizer,
  onShareSector,
  onEditBoundary,
  onOpenSector,
  onDeleteSector,
  onSendCommand,
  isSelected = false,
}: SectorManagementCardProps) {
  const [showParticipants, setShowParticipants] = useState(false)
  const [showActions, setShowActions] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const statusColors: Record<string, string> = {
    active: 'bg-green-500',
    alert: 'bg-orange-500',
    moving: 'bg-blue-500',
    offline: 'bg-gray-500',
  }

  const statusTextColors: Record<string, string> = {
    active: 'text-green-400',
    alert: 'text-orange-400',
    moving: 'text-blue-400',
    offline: 'text-gray-400',
  }

  const handlePauseRoom = () => {
    if (onSendCommand) {
      onSendCommand({ type: 'pause_room' })
    }
  }

  const handleResumeRoom = () => {
    if (onSendCommand) {
      onSendCommand({ type: 'resume_room' })
    }
  }

  const handleSetAlert = () => {
    if (onSendCommand) {
      onSendCommand({ type: 'set_alert_status', status: 'alert' })
    }
  }

  return (
    <div
      className={`bg-white/5 rounded-lg overflow-hidden transition-all
        ${isSelected ? 'ring-2 ring-primary' : 'hover:bg-white/10'}`}
    >
      {/* Header */}
      <div
        className="p-3 cursor-pointer"
        onClick={() => onOpenSector?.()}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <span className={`w-2.5 h-2.5 rounded-full ${statusColors[sector.status]} flex-shrink-0`} />
            <span className="text-sm font-medium text-white truncate">{sector.name}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={`text-xs uppercase ${statusTextColors[sector.status]}`}>
              {sector.status}
            </span>
          </div>
        </div>

        {/* Quick stats */}
        <div className="flex items-center gap-3 mt-2 text-xs text-white/60">
          <span className="flex items-center gap-1">
            <span>👥</span> {sector.participantCount}
          </span>
          {sector.radiusMeters && (
            <span className="flex items-center gap-1">
              <span>📍</span> {sector.radiusMeters}m
            </span>
          )}
          {sector.pendingEscalations > 0 && (
            <span className="flex items-center gap-1 text-red-400">
              <span>⚠️</span> {sector.pendingEscalations}
            </span>
          )}
        </div>

        {/* Organizer info */}
        {sector.leadName && (
          <p className="text-xs text-white/40 mt-1.5 truncate">
            Lead: {sector.leadName}
          </p>
        )}
      </div>

      {/* Action buttons */}
      <div className="px-3 pb-2">
        <div className="flex gap-1.5">
          <button
            onClick={(e) => { e.stopPropagation(); setShowParticipants(!showParticipants) }}
            className="flex-1 px-2 py-1.5 bg-white/5 hover:bg-white/10 text-white/70
              rounded text-xs font-medium transition-colors"
          >
            👥 {showParticipants ? 'Hide' : 'People'}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setShowActions(!showActions) }}
            className="flex-1 px-2 py-1.5 bg-white/5 hover:bg-white/10 text-white/70
              rounded text-xs font-medium transition-colors"
          >
            ⚙️ {showActions ? 'Hide' : 'Actions'}
          </button>
          {onShareSector && (
            <button
              onClick={(e) => { e.stopPropagation(); onShareSector() }}
              className="px-2 py-1.5 bg-primary/20 hover:bg-primary/30 text-primary
                rounded text-xs font-medium transition-colors"
            >
              🔗
            </button>
          )}
        </div>
      </div>

      {/* Participants panel */}
      {showParticipants && (
        <div className="border-t border-white/10 p-3 bg-black/20">
          <h4 className="text-xs font-medium text-white/60 mb-2 uppercase tracking-wider">
            Participants ({participants.length})
          </h4>
          {participants.length === 0 ? (
            <p className="text-xs text-white/40 italic">No participants in sector</p>
          ) : (
            <div className="space-y-1.5 max-h-32 overflow-y-auto">
              {participants.map((p) => (
                <div
                  key={p.peerId}
                  className="flex items-center justify-between gap-2 py-1 px-2 bg-white/5 rounded"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0
                      ${p.isInsideGeofence ? 'bg-green-500' : 'bg-yellow-500'}`}
                    />
                    <span className="text-xs text-white truncate">{p.displayName}</span>
                    {p.role !== 'participant' && (
                      <span className="text-[10px] px-1 py-0.5 rounded bg-primary/20 text-primary uppercase">
                        {p.role === 'organizer' ? 'Lead' : 'Coord'}
                      </span>
                    )}
                  </div>
                  {onAssignOrganizer && p.role === 'participant' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onAssignOrganizer(p.peerId, '') // publicKey would need to be fetched
                      }}
                      className="text-[10px] px-1.5 py-0.5 bg-teal/20 hover:bg-teal/30
                        text-teal rounded transition-colors"
                    >
                      Make Lead
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Actions panel */}
      {showActions && (
        <div className="border-t border-white/10 p-3 bg-black/20">
          <h4 className="text-xs font-medium text-white/60 mb-2 uppercase tracking-wider">
            Remote Controls
          </h4>
          <div className="grid grid-cols-2 gap-2">
            {onEditBoundary && (
              <button
                onClick={(e) => { e.stopPropagation(); onEditBoundary() }}
                className="px-2 py-1.5 bg-white/5 hover:bg-white/10 text-white/70
                  rounded text-xs font-medium transition-colors flex items-center justify-center gap-1"
              >
                📐 Boundary
              </button>
            )}
            {onSendCommand && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); handleSetAlert() }}
                  className="px-2 py-1.5 bg-orange-500/20 hover:bg-orange-500/30 text-orange-400
                    rounded text-xs font-medium transition-colors flex items-center justify-center gap-1"
                >
                  🚨 Alert
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handlePauseRoom() }}
                  className="px-2 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400
                    rounded text-xs font-medium transition-colors flex items-center justify-center gap-1"
                >
                  ⏸️ Pause
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleResumeRoom() }}
                  className="px-2 py-1.5 bg-green-500/20 hover:bg-green-500/30 text-green-400
                    rounded text-xs font-medium transition-colors flex items-center justify-center gap-1"
                >
                  ▶️ Resume
                </button>
              </>
            )}
          </div>

          {/* Delete sector */}
          {onDeleteSector && (
            <div className="mt-3 pt-3 border-t border-white/10">
              {!confirmDelete ? (
                <button
                  onClick={(e) => { e.stopPropagation(); setConfirmDelete(true) }}
                  className="w-full px-2 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400
                    rounded text-xs font-medium transition-colors flex items-center justify-center gap-1
                    border border-red-500/20"
                >
                  🗑️ Delete Sector
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onDeleteSector()
                      setConfirmDelete(false)
                    }}
                    className="flex-1 px-2 py-1.5 bg-red-500 hover:bg-red-600 text-white
                      rounded text-xs font-medium transition-colors"
                  >
                    Confirm Delete
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setConfirmDelete(false) }}
                    className="flex-1 px-2 py-1.5 bg-white/10 hover:bg-white/20 text-white/70
                      rounded text-xs font-medium transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Boundary drift indicator */}
      {sector.participantCentroidLat !== undefined &&
       sector.participantCentroidLng !== undefined &&
       sector.centerLat !== undefined &&
       sector.centerLng !== undefined && (
        <BoundaryDriftIndicator
          centerLat={sector.centerLat}
          centerLng={sector.centerLng}
          centroidLat={sector.participantCentroidLat}
          centroidLng={sector.participantCentroidLng}
          radiusMeters={sector.radiusMeters ?? 100}
        />
      )}
    </div>
  )
}

// ============= Sub-components =============

function BoundaryDriftIndicator({
  centerLat,
  centerLng,
  centroidLat,
  centroidLng,
  radiusMeters,
}: {
  centerLat: number
  centerLng: number
  centroidLat: number
  centroidLng: number
  radiusMeters: number
}) {
  // Calculate drift distance (simple approximation)
  const R = 6371000 // Earth radius in meters
  const dLat = (centroidLat - centerLat) * Math.PI / 180
  const dLon = (centroidLng - centerLng) * Math.PI / 180
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(centerLat * Math.PI / 180) * Math.cos(centroidLat * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  const drift = R * c

  // Only show if drift is significant (>25% of radius)
  const driftPercentage = (drift / radiusMeters) * 100
  if (driftPercentage < 25) return null

  const isSignificant = driftPercentage > 50
  const color = isSignificant ? 'text-orange-400 bg-orange-500/10' : 'text-yellow-400 bg-yellow-500/10'

  return (
    <div className={`px-3 py-1.5 text-[10px] ${color} border-t border-white/10`}>
      <span className="font-medium">
        {isSignificant ? '⚠️' : '📍'} Participants {Math.round(drift)}m from center ({Math.round(driftPercentage)}% drift)
      </span>
    </div>
  )
}

export default SectorManagementCard
